/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useRef, useState } from "react";
import {
    Award,
    BarChart3,
    CheckCircle2,
    Loader2,
    Lock,
    PlayCircle,
    RefreshCw,
    Sparkles,
    Timer,
    X,
    XCircle,
    Zap,
} from "lucide-react";
import { clientApi } from "@/lib/clientAuth";

const SERIES_ICON = ["", "01", "02", "03", "04", "05"];

const SCORE_BANDS = (n) =>
    n >= 70 ? { color: "#7fb069", label: "Excellent" }
    : n >= 50 ? { color: "#e6a23c", label: "Préparation recommandée" }
    : { color: "#e89090", label: "Niveau insuffisant" };

const CRITERIA = [
    { k: "clarte", l: "Clarté" },
    { k: "coherence", l: "Cohérence" },
    { k: "credibilite", l: "Crédibilité" },
    { k: "vision", l: "Vision" },
    { k: "finance", l: "Finance" },
    { k: "conviction", l: "Conviction" },
    { k: "preparation", l: "Préparation" },
];

// ─────────────────────────────────────────────────────────────
// ROOT — appears in ClientEspace after other modules
// ─────────────────────────────────────────────────────────────
export default function ClientCommitteeSimulator() {
    const [state, setState] = useState(null);
    const [session, setSession] = useState(null);
    const [loadingSeries, setLoadingSeries] = useState(null);
    const [history, setHistory] = useState([]);
    const [historyOpen, setHistoryOpen] = useState(false);

    const reloadState = async () => {
        try {
            const { data } = await clientApi.get("/committee/state");
            setState(data);
        } catch (e) {
            setState({ unlocked: false });
        }
    };
    const reloadHistory = async () => {
        try {
            const { data } = await clientApi.get("/committee/history");
            setHistory(data.items || []);
        } catch (e) {
            // history is non-critical
        }
    };

    useEffect(() => {
        reloadState();
        reloadHistory();
    }, []);

    if (!state) return null;
    if (!state.unlocked) return <LockedCard reason={state.reason} />;

    const startSession = async (series, mode) => {
        setLoadingSeries(series);
        try {
            const { data } = await clientApi.post("/committee/sessions", {
                series,
                mode,
            });
            setSession(data);
        } catch (e) {
            alert(e?.response?.data?.detail || "Démarrage impossible");
        } finally {
            setLoadingSeries(null);
        }
    };

    const resumeSession = async (sessionId) => {
        const { data } = await clientApi.get(`/committee/sessions/${sessionId}`);
        setSession(data);
    };

    return (
        <section data-testid="client-committee" className="mb-16 space-y-6">
            <header className="bg-[#070d18] border border-[#d4af37]/20 p-10 lg:p-12">
                <div className="overline text-[#d4af37]/80 mb-3">
                    <Sparkles
                        strokeWidth={1}
                        size={13}
                        className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                    />
                    XV Investment Committee Simulator™
                </div>
                <h2
                    className="font-serif text-[26px] sm:text-[32px] text-[#f4ecd8] leading-tight mb-3"
                    style={{ fontWeight: 400 }}
                >
                    Préparation Investisseurs
                </h2>
                <p className="text-[13.5px] text-[#f4ecd8]/60 italic font-light leading-[1.95] max-w-3xl mb-8">
                    Cinq séries de vingt questions pour vous préparer aux
                    rendez-vous avec investisseurs, family offices, banques
                    privées et comités d'investissement. L'IA s'appuie sur
                    votre dossier pour personnaliser chaque entretien.
                </p>

                {state.certified && <CertificationBadge score={state.average_score} />}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                    <Kpi label="Moyenne" value={`${state.average_score} %`} />
                    <Kpi
                        label="Séries validées"
                        value={`${state.series.filter((s) => s.validated).length}/5`}
                    />
                    <Kpi
                        label="Tentatives totales"
                        value={state.series.reduce((acc, s) => acc + s.attempts, 0)}
                    />
                    <Kpi label="Seuil" value={`${state.threshold} %`} />
                </div>
            </header>

            <div className="space-y-3">
                {state.series.map((s) => (
                    <SeriesRow
                        key={s.series}
                        s={s}
                        loading={loadingSeries === s.series}
                        onStart={(mode) => startSession(s.series, mode)}
                        onResume={() => s.active_session_id && resumeSession(s.active_session_id)}
                    />
                ))}
            </div>

            <button
                type="button"
                onClick={() => setHistoryOpen((o) => !o)}
                data-testid="committee-history-toggle"
                className="text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70 hover:text-[#d4af37] inline-flex items-center gap-2"
            >
                <BarChart3 strokeWidth={1.3} size={13} />
                {historyOpen ? "Masquer l'historique" : "Voir l'historique complet"}
            </button>

            {historyOpen && <HistoryView items={history} onOpen={resumeSession} />}

            {session && (
                <SimulationModal
                    session={session}
                    setSession={setSession}
                    onClose={async () => {
                        setSession(null);
                        await reloadState();
                        await reloadHistory();
                    }}
                />
            )}
        </section>
    );
}

function LockedCard({ reason }) {
    return (
        <section
            data-testid="client-committee-locked"
            className="mb-16 bg-[#070d18] border border-white/[0.06] p-10 lg:p-12"
        >
            <div className="overline text-[#f4ecd8]/40 mb-3">
                <Lock
                    strokeWidth={1}
                    size={13}
                    className="inline-block mr-2 -mt-0.5"
                />
                Préparation Investisseurs
            </div>
            <h2
                className="font-serif text-[22px] sm:text-[26px] text-[#f4ecd8]/55 leading-tight mb-2"
                style={{ fontWeight: 400 }}
            >
                Module verrouillé
            </h2>
            <p className="text-[13px] text-[#f4ecd8]/45 italic font-light leading-relaxed max-w-2xl">
                {reason ||
                    "Le simulateur s'activera automatiquement à l'étape « Discussions confidentielles »."}
            </p>
        </section>
    );
}

function Kpi({ label, value }) {
    return (
        <div className="border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="overline text-[#d4af37]/70 mb-2">{label}</div>
            <div
                className="font-serif text-[22px] text-[#f4ecd8]"
                style={{ fontWeight: 400 }}
            >
                {value}
            </div>
        </div>
    );
}

function CertificationBadge({ score }) {
    return (
        <div
            data-testid="committee-certification"
            className="inline-flex items-center gap-4 border border-[#d4af37] bg-[#d4af37]/[0.08] px-6 py-4"
        >
            <Award strokeWidth={1.2} size={26} className="text-[#d4af37]" />
            <div>
                <div className="font-serif text-[18px] text-[#f4ecd8] leading-tight">
                    XV INVESTMENT READY
                </div>
                <div className="text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/85 mt-1">
                    Certifié — moyenne {score} %
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// SERIES ROW
// ─────────────────────────────────────────────────────────────
function SeriesRow({ s, loading, onStart, onResume }) {
    const [showStart, setShowStart] = useState(false);
    const band = s.best_score > 0 ? SCORE_BANDS(s.best_score) : null;

    return (
        <div
            data-testid={`committee-series-${s.series}`}
            className={`border bg-[#070d18] p-6 lg:p-8 ${
                s.validated
                    ? "border-[#d4af37]/40"
                    : "border-white/[0.08]"
            }`}
        >
            <div className="flex items-start gap-6">
                <div
                    className={`font-serif text-[42px] leading-none ${
                        s.validated ? "text-[#d4af37]" : "text-[#f4ecd8]/30"
                    }`}
                    style={{ fontWeight: 300 }}
                >
                    {SERIES_ICON[s.series]}
                </div>
                <div className="flex-1 min-w-0">
                    <h3
                        className="font-serif text-[19px] text-[#f4ecd8] mb-1"
                        style={{ fontWeight: 400 }}
                    >
                        {s.title}
                    </h3>
                    <p className="text-[12px] text-[#f4ecd8]/55 italic mb-3 leading-snug">
                        {s.subtitle}
                    </p>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-[10px] tracking-[0.24em] uppercase text-[#f4ecd8]/45">
                        <span>{s.attempts} tentative{s.attempts > 1 ? "s" : ""}</span>
                        {s.best_score > 0 && (
                            <span style={{ color: band.color }}>
                                Meilleur score · {s.best_score} %
                            </span>
                        )}
                        {s.validated && (
                            <span className="text-[#d4af37]">✓ Validée</span>
                        )}
                    </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                    {s.active_session_id ? (
                        <button
                            type="button"
                            onClick={onResume}
                            data-testid={`committee-resume-${s.series}`}
                            className="px-5 py-2 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                        >
                            Reprendre
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setShowStart((v) => !v)}
                            disabled={loading}
                            data-testid={`committee-start-${s.series}`}
                            className="px-5 py-2 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                        >
                            {loading ? "Génération…" : s.attempts > 0 ? "Recommencer" : "Démarrer"}
                        </button>
                    )}
                </div>
            </div>

            {showStart && (
                <div className="mt-6 pt-6 border-t border-white/[0.06] flex flex-wrap items-center gap-4">
                    <span className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/55">
                        Choisir le mode :
                    </span>
                    <button
                        type="button"
                        onClick={() => onStart("standard")}
                        disabled={loading}
                        data-testid={`committee-mode-standard-${s.series}`}
                        className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.24em] uppercase border border-white/15 text-[#f4ecd8]/80 hover:border-[#d4af37] hover:text-[#d4af37] transition-all duration-500"
                    >
                        <PlayCircle strokeWidth={1.3} size={13} />
                        Mode Standard
                    </button>
                    <button
                        type="button"
                        onClick={() => onStart("requin")}
                        disabled={loading}
                        data-testid={`committee-mode-requin-${s.series}`}
                        className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.24em] uppercase border border-[#e89090]/50 text-[#e89090]/90 hover:bg-[#e89090]/10 hover:border-[#e89090] hover:text-[#e89090] transition-all duration-500"
                    >
                        <Zap strokeWidth={1.3} size={13} />
                        Mode Requin
                    </button>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// SIMULATION MODAL — immersive
// ─────────────────────────────────────────────────────────────
function SimulationModal({ session, setSession, onClose }) {
    const [answer, setAnswer] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [lastFeedback, setLastFeedback] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const startTime = useRef(Date.now());

    useEffect(() => {
        startTime.current = Date.now();
        setAnswer("");
        // Note: do NOT reset showFeedback here — the feedback for the
        // previous answer must remain visible alongside the new question
        // until the user explicitly dismisses it via "Continuer".
    }, [session?.current_index, session?.id]);

    const isRequin = session.mode === "requin";
    const q = session.current_question;
    const completed = session.status !== "in_progress";

    const submit = async () => {
        if (!answer.trim()) return;
        setSubmitting(true);
        const duration = Math.round((Date.now() - startTime.current) / 1000);
        try {
            const { data } = await clientApi.post(
                `/committee/sessions/${session.id}/answer`,
                { answer: answer.trim(), duration_seconds: duration },
            );
            const lastAns = data.answers?.[data.answers.length - 1];
            setLastFeedback(lastAns?.scores || null);
            setShowFeedback(true);
            setSession(data);
        } catch (e) {
            alert(e?.response?.data?.detail || "Échec de soumission");
        } finally {
            setSubmitting(false);
        }
    };

    const abandon = async () => {
        if (!window.confirm("Abandonner cette session ? La progression sera perdue.")) return;
        try {
            await clientApi.post(`/committee/sessions/${session.id}/abandon`);
        } catch (e) {
            // best-effort
        }
        onClose();
    };

    return (
        <div
            data-testid="committee-simulation-modal"
            className="fixed inset-0 z-50 bg-[#040810]/97 backdrop-blur-sm overflow-y-auto"
        >
            <div className="max-w-4xl mx-auto px-6 py-12">
                {/* Header */}
                <div className="flex items-start justify-between mb-10 gap-6">
                    <div>
                        <div className="overline text-[#d4af37]/75 mb-2 flex items-center gap-3">
                            <span>Série {session.series} · Tentative {session.attempt}</span>
                            {isRequin && (
                                <span className="text-[#e89090] inline-flex items-center gap-1.5">
                                    <Zap strokeWidth={1.5} size={11} /> Mode Requin
                                </span>
                            )}
                        </div>
                        <h2
                            className="font-serif text-[28px] text-[#f4ecd8] leading-tight"
                            style={{ fontWeight: 400 }}
                        >
                            Comité d'investissement XV
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={completed ? onClose : abandon}
                        data-testid="committee-close-modal"
                        className="text-[#f4ecd8]/40 hover:text-[#f4ecd8] p-2"
                    >
                        <X strokeWidth={1.4} size={22} />
                    </button>
                </div>

                {/* Progress */}
                <ProgressBar
                    index={session.current_index}
                    total={session.total_questions}
                    isRequin={isRequin}
                />

                {/* Question or final report */}
                {!completed && q && (
                    <QuestionView
                        question={q}
                        answer={answer}
                        setAnswer={setAnswer}
                        submitting={submitting}
                        onSubmit={submit}
                        isRequin={isRequin}
                        showFeedback={showFeedback && lastFeedback}
                        feedback={lastFeedback}
                        onDismissFeedback={() => setShowFeedback(false)}
                    />
                )}

                {completed && <FinalResult session={session} onClose={onClose} />}
            </div>
        </div>
    );
}

function ProgressBar({ index, total, isRequin }) {
    const pct = total > 0 ? Math.round((index / total) * 100) : 0;
    return (
        <div className="mb-10">
            <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/55">
                    Question {Math.min(index + 1, total)} / {total}
                </span>
                <span className="text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70">
                    {pct} %
                </span>
            </div>
            <div className="h-[2px] bg-white/[0.08] relative overflow-hidden">
                <div
                    className="absolute inset-y-0 left-0 transition-all duration-700"
                    style={{
                        width: `${pct}%`,
                        background: isRequin ? "#e89090" : "#d4af37",
                    }}
                />
            </div>
        </div>
    );
}

function QuestionView({
    question,
    answer,
    setAnswer,
    submitting,
    onSubmit,
    isRequin,
    showFeedback,
    feedback,
    onDismissFeedback,
}) {
    const elapsed = useElapsed();
    return (
        <div className="space-y-8">
            <div
                data-testid="committee-question"
                className={`border-l-2 pl-6 ${
                    isRequin ? "border-[#e89090]/60" : "border-[#d4af37]/60"
                }`}
            >
                <div className="overline text-[#d4af37]/65 mb-2">
                    {question.theme || `Question ${question.index}`}
                    {question.coefficient > 1 && (
                        <span className="ml-3 text-[#d4af37]">
                            · Coefficient {question.coefficient}
                        </span>
                    )}
                </div>
                <p
                    className="font-serif text-[22px] sm:text-[26px] text-[#f4ecd8] leading-[1.45]"
                    style={{ fontWeight: 400 }}
                >
                    {question.text}
                </p>
            </div>

            {showFeedback && feedback && (
                <FeedbackPanel feedback={feedback} onDismiss={onDismissFeedback} />
            )}

            <div>
                <div className="flex items-center justify-between mb-3">
                    <span className="overline text-[#d4af37]/70">Votre réponse</span>
                    <span className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/40 inline-flex items-center gap-2">
                        <Timer strokeWidth={1.3} size={12} /> {fmtTime(elapsed)}
                    </span>
                </div>
                <textarea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    rows={8}
                    placeholder="Exprimez votre réponse avec précision. Évitez les généralités."
                    data-testid="committee-answer-input"
                    className="w-full bg-transparent border border-white/12 focus:border-[#d4af37] outline-none p-5 text-[15px] font-serif text-[#f4ecd8] resize-none leading-[1.85]"
                />
                <div className="mt-4 flex items-center justify-end gap-4">
                    <button
                        type="button"
                        onClick={onSubmit}
                        disabled={submitting || !answer.trim()}
                        data-testid="committee-submit-answer"
                        className={`inline-flex items-center gap-2 px-6 py-3 text-[10px] tracking-[0.30em] uppercase border transition-all duration-500 disabled:opacity-40 ${
                            isRequin
                                ? "border-[#e89090] text-[#e89090] hover:bg-[#e89090] hover:text-[#070d18]"
                                : "border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18]"
                        }`}
                    >
                        {submitting ? (
                            <Loader2 strokeWidth={1.5} size={14} className="animate-spin" />
                        ) : null}
                        Soumettre la réponse
                    </button>
                </div>
            </div>
        </div>
    );
}

function FeedbackPanel({ feedback, onDismiss }) {
    const band = SCORE_BANDS(feedback.average);
    return (
        <div
            data-testid="committee-feedback"
            className="border border-[#d4af37]/30 bg-[#d4af37]/[0.04] p-6"
        >
            <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
                <div className="overline text-[#d4af37]/85">
                    Évaluation de la dernière réponse
                </div>
                <button
                    type="button"
                    onClick={onDismiss}
                    className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/45 hover:text-[#f4ecd8]"
                >
                    Continuer
                </button>
            </div>
            <div className="flex items-baseline gap-3 mb-5">
                <span
                    className="font-serif text-[44px] leading-none"
                    style={{ color: band.color, fontWeight: 300 }}
                >
                    {feedback.average}
                </span>
                <span className="text-[10px] tracking-[0.30em] uppercase" style={{ color: band.color }}>
                    {band.label}
                </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {CRITERIA.map((c) => (
                    <div key={c.k} className="border border-white/[0.06] px-3 py-2">
                        <div className="text-[9px] tracking-[0.26em] uppercase text-[#f4ecd8]/45 mb-1">
                            {c.l}
                        </div>
                        <div className="font-serif text-[16px] text-[#f4ecd8]">
                            {feedback[c.k]}
                        </div>
                    </div>
                ))}
            </div>
            {feedback.comment && (
                <p className="text-[13px] text-[#f4ecd8]/80 leading-[1.8] italic font-light border-l border-[#d4af37]/40 pl-4">
                    {feedback.comment}
                </p>
            )}
        </div>
    );
}

function FinalResult({ session, onClose }) {
    const band = SCORE_BANDS(session.score);
    const validated = session.status === "completed";
    const r = session.report || {};
    return (
        <div data-testid="committee-final-result" className="space-y-10">
            <div className="text-center py-8">
                {validated ? (
                    <CheckCircle2
                        strokeWidth={1}
                        size={56}
                        className="text-[#d4af37] mx-auto mb-4"
                    />
                ) : (
                    <XCircle
                        strokeWidth={1}
                        size={56}
                        className="text-[#e89090] mx-auto mb-4"
                    />
                )}
                <div className="overline text-[#d4af37]/75 mb-3">
                    Score final · Série {session.series}
                </div>
                <div
                    className="font-serif text-[72px] leading-none mb-3"
                    style={{ color: band.color, fontWeight: 300 }}
                >
                    {session.score} %
                </div>
                <div
                    className="text-[11px] tracking-[0.32em] uppercase"
                    style={{ color: band.color }}
                >
                    {band.label}
                </div>
                {!validated && (
                    <p className="mt-6 text-[13px] text-[#f4ecd8]/55 italic font-light max-w-xl mx-auto">
                        Seuil de validation : 70 %. Vous pouvez relancer la
                        série pour améliorer votre score.
                    </p>
                )}
            </div>

            {r.forces && r.forces.length > 0 && (
                <ReportBlock title="Forces" items={r.forces} accent />
            )}
            {r.faiblesses && r.faiblesses.length > 0 && (
                <ReportBlock title="Faiblesses" items={r.faiblesses} />
            )}
            {r.questions_mal_maitrisees && r.questions_mal_maitrisees.length > 0 && (
                <ReportBlock
                    title="Questions mal maîtrisées"
                    items={r.questions_mal_maitrisees}
                />
            )}
            {r.axes_amelioration && r.axes_amelioration.length > 0 && (
                <ReportBlock
                    title="Axes d'amélioration prioritaires"
                    items={r.axes_amelioration}
                    accent
                />
            )}
            {r.objections_probables && r.objections_probables.length > 0 && (
                <ReportBlock
                    title="Objections probables des investisseurs"
                    items={r.objections_probables}
                />
            )}
            {r.suggestions_reponses && r.suggestions_reponses.length > 0 && (
                <ReportBlock
                    title="Suggestions d'angles de réponse"
                    items={r.suggestions_reponses}
                    accent
                />
            )}
            {r.documents_a_preparer && r.documents_a_preparer.length > 0 && (
                <ReportBlock
                    title="Documents à préparer avant les rendez-vous"
                    items={r.documents_a_preparer}
                />
            )}

            <div className="text-center pt-6">
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="committee-close-result"
                    className="inline-flex items-center gap-2 px-8 py-3 text-[10px] tracking-[0.30em] uppercase border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                >
                    <RefreshCw strokeWidth={1.3} size={13} />
                    Retour à la préparation
                </button>
            </div>
        </div>
    );
}

function ReportBlock({ title, items, accent }) {
    return (
        <div className="border-l-2 border-[#d4af37]/40 pl-6">
            <div
                className={`overline mb-3 ${
                    accent ? "text-[#d4af37]" : "text-[#f4ecd8]/65"
                }`}
            >
                {title}
            </div>
            <ul className="space-y-2">
                {items.map((it, i) => (
                    <li
                        key={i}
                        className="text-[13.5px] text-[#f4ecd8]/82 font-light leading-[1.85]"
                    >
                        — {it}
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// HISTORY
// ─────────────────────────────────────────────────────────────
function HistoryView({ items, onOpen }) {
    if (items.length === 0) {
        return (
            <p className="text-[12px] text-[#f4ecd8]/40 italic">
                Aucune tentative pour le moment.
            </p>
        );
    }
    return (
        <div data-testid="committee-history" className="space-y-4">
            <ProgressChart items={items} />
            <ul className="space-y-2">
                {items.map((s) => {
                    const band = s.score > 0 ? SCORE_BANDS(s.score) : null;
                    return (
                        <li
                            key={s.id}
                            data-testid={`committee-history-${s.id}`}
                            className="flex items-center gap-4 border border-white/[0.06] bg-white/[0.02] px-4 py-3 cursor-pointer hover:bg-white/[0.04]"
                            onClick={() => onOpen(s.id)}
                        >
                            <span
                                className="text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/70 w-20"
                            >
                                Série {s.series}
                            </span>
                            <span className="text-[11px] text-[#f4ecd8]/55 w-24">
                                Tentative {s.attempt}
                            </span>
                            <span className="text-[11px] text-[#f4ecd8]/40 flex-1">
                                {s.started_at
                                    ? new Date(s.started_at).toLocaleString("fr-FR", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                      })
                                    : ""}
                            </span>
                            <span className="text-[10px] tracking-[0.24em] uppercase text-[#f4ecd8]/50 w-24 text-right">
                                {s.mode === "requin" ? "Mode Requin" : "Standard"}
                            </span>
                            <span
                                className="font-serif text-[16px] w-16 text-right"
                                style={{ color: band ? band.color : "#f4ecd8" }}
                            >
                                {s.score} %
                            </span>
                            <span className="text-[10px] tracking-[0.26em] uppercase w-24 text-right">
                                <StatusPill status={s.status} />
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        in_progress: { l: "En cours", c: "#e6a23c" },
        completed: { l: "Validée", c: "#7fb069" },
        failed: { l: "Insuffisante", c: "#e89090" },
        abandoned: { l: "Abandonnée", c: "#f4ecd8" },
    };
    const m = map[status] || { l: status, c: "#f4ecd8" };
    return <span style={{ color: m.c }}>{m.l}</span>;
}

function ProgressChart({ items }) {
    const points = useMemo(() => {
        const completed = items
            .filter((i) => i.status === "completed" || i.status === "failed")
            .slice()
            .reverse();
        return completed.map((s, i) => ({
            x: i,
            y: s.score || 0,
            series: s.series,
            mode: s.mode,
        }));
    }, [items]);

    if (points.length < 2) return null;
    const w = 720;
    const h = 140;
    const px = (i) => 30 + (i * (w - 60)) / (points.length - 1);
    const py = (v) => h - 20 - ((v / 100) * (h - 40));
    const path = points
        .map((p, i) => `${i === 0 ? "M" : "L"} ${px(i)} ${py(p.y)}`)
        .join(" ");
    return (
        <svg
            viewBox={`0 0 ${w} ${h}`}
            className="w-full border border-white/[0.06] bg-white/[0.02] p-4"
            data-testid="committee-progress-chart"
        >
            <line
                x1={30}
                x2={w - 30}
                y1={py(70)}
                y2={py(70)}
                stroke="#d4af37"
                strokeOpacity="0.25"
                strokeDasharray="3 4"
            />
            <text x={w - 28} y={py(70) - 4} fontSize="9" fill="#d4af37" opacity="0.5">
                70 %
            </text>
            <path d={path} stroke="#d4af37" strokeWidth="1.5" fill="none" />
            {points.map((p, i) => (
                <circle
                    key={i}
                    cx={px(i)}
                    cy={py(p.y)}
                    r="3.5"
                    fill={p.mode === "requin" ? "#e89090" : "#d4af37"}
                />
            ))}
        </svg>
    );
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function useElapsed() {
    const [n, setN] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setN((v) => v + 1), 1000);
        return () => clearInterval(id);
    }, []);
    return n;
}

function fmtTime(s) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}
